import * as THREE from 'three';
import type { ColorId, GameState, Pos } from '@color-dots/game';
import type { VisualStyle } from '../game/visualStyles.js';
import { BoardMesh } from './BoardMesh.js';
import { CameraRig, type ViewportInsets } from './CameraRig.js';
import { CORE_Y, CoreSphere3d } from './CoreSphere3d.js';
import { DotsMeshes } from './DotsMeshes.js';
import { GearBackground3d } from './GearBackground3d.js';
import { GearRailsMesh } from './GearRailsMesh.js';
import { Lighting } from './Lighting.js';
import { PathsMeshes } from './PathsMeshes.js';
import { RaycastPicker, type SphereBounds } from './RaycastPicker.js';
import { RotationController } from './RotationController.js';
import { TouchIndicator } from './TouchIndicator.js';
import { getSceneTheme3D } from './sceneTheme3d.js';

export interface Scene3DOptions {
  /** Word Connect: words in cells, no colored endpoint spheres */
  wordMode?: boolean;
  /** Visual style from level rotation (pipe, gear, …) */
  visualStyle?: VisualStyle;
}

/** Radial lift used to place pickable cell hotspots on the glass sphere's surface, matching endpoint/node visuals. */
const PICK_LIFT = 0.14;

export class Scene3DApp {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly boardGroup = new THREE.Group();
  readonly picker = new RaycastPicker();
  readonly rotation: RotationController;

  private cameraRig: CameraRig;
  private lighting: Lighting;
  private core: CoreSphere3d;
  private board: BoardMesh;
  private dots: DotsMeshes;
  private paths: PathsMeshes;
  private rails: GearRailsMesh;
  private gearBackground: GearBackground3d;
  private touchIndicator: TouchIndicator;
  private sphereBounds: SphereBounds = { center: new THREE.Vector3(0, 0, 0), radius: 0, lift: PICK_LIFT };
  private rafId = 0;
  boardSize = 5;
  private viewportInsets?: ViewportInsets;
  private wordMode = false;
  private visualStyle: VisualStyle = 'pipe';
  onAfterRender: (() => void) | null = null;

  constructor(private canvas: HTMLCanvasElement, options: Scene3DOptions = {}) {
    this.wordMode = options.wordMode ?? false;
    this.visualStyle = options.visualStyle ?? 'pipe';
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || window.innerWidth;
    const h = rect.height || window.innerHeight;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.cameraRig = new CameraRig(w / h);
    this.lighting = new Lighting(this.scene);
    this.core = new CoreSphere3d();
    this.board = new BoardMesh();
    this.dots = new DotsMeshes();
    this.paths = new PathsMeshes();
    this.rails = new GearRailsMesh();
    this.gearBackground = new GearBackground3d();
    this.touchIndicator = new TouchIndicator(this.scene);

    this.board.setVisualStyle(this.visualStyle);
    this.dots.setVisualStyle(this.visualStyle);
    this.paths.setVisualStyle(this.visualStyle);

    this.scene.add(this.gearBackground.group);
    this.boardGroup.add(this.core.group);
    this.boardGroup.add(this.rails.group);
    this.boardGroup.add(this.board.group);
    this.boardGroup.add(this.dots.group);
    this.boardGroup.add(this.paths.group);
    this.dots.group.visible = !this.wordMode;
    this.updateGearDecor(this.visualStyle === 'gear');
    this.scene.add(this.boardGroup);

    this.rotation = new RotationController(this.boardGroup);
    this.applyTheme();
    this.startLoop();
  }

  build(state: GameState): void {
    this.boardSize = state.size;
    this.core.build(state.size, getSceneTheme3D(this.visualStyle));
    const sphereCenter = new THREE.Vector3(0, CORE_Y, 0);
    const sphereRadius = this.core.getRadius();
    this.board.setSphereBounds(sphereCenter, sphereRadius);
    this.board.build(state);
    this.dots.setSize(state.size);
    this.paths.setSphereBounds(sphereCenter, sphereRadius);
    this.dots.setSphereBounds(sphereCenter, sphereRadius);
    this.sphereBounds = { center: sphereCenter, radius: sphereRadius, lift: PICK_LIFT };
    this.rebuildGearDecor(state.size);
    this.sync(state, null);
    this.fitCamera();
  }

  sync(state: GameState, activeColor: ColorId | null): void {
    this.board.updateCells(state);
    if (!this.wordMode) {
      this.dots.sync(state, activeColor);
    }
    this.paths.sync(state, activeColor);
  }

  getVisualStyle(): VisualStyle {
    return this.visualStyle;
  }

  setVisualStyle(style: VisualStyle): void {
    this.visualStyle = style;
    this.board.setVisualStyle(style);
    this.dots.setVisualStyle(style);
    this.paths.setVisualStyle(style);
    this.updateGearDecor(style === 'gear');
    this.rebuildGearDecor(this.boardSize);
    this.applyTheme();
  }

  private updateGearDecor(visible: boolean): void {
    this.rails.group.visible = visible;
    this.gearBackground.group.visible = visible;
  }

  private rebuildGearDecor(size: number): void {
    if (this.visualStyle !== 'gear' || size === 0) {
      this.rails.dispose();
      this.gearBackground.dispose();
      return;
    }
    const theme = getSceneTheme3D('gear');
    this.rails.build(size, theme);
    this.gearBackground.build(size);
  }

  applyTheme(): void {
    const theme = getSceneTheme3D(this.visualStyle);
    this.scene.background = new THREE.Color(theme.background);
    if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.color.setHex(theme.fogColor);
      this.scene.fog.density = theme.fogDensity;
    } else {
      this.scene.fog = new THREE.FogExp2(theme.fogColor, theme.fogDensity);
    }
    this.lighting.applyTheme(theme);
    this.board.applyTheme(theme);
    this.core.applyTheme(theme);
    if (this.visualStyle === 'gear') {
      this.rails.applyTheme(theme);
    }
  }

  resize(insets?: ViewportInsets): void {
    if (insets) this.viewportInsets = insets;
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    this.renderer.setSize(w, h, false);
    this.cameraRig.resize(w, h, this.boardSize, this.viewportInsets);
    this.lighting.updateForCamera(this.cameraRig.camera);
  }

  cycleView(insets?: ViewportInsets): void {
    const parent = this.canvas.parentElement;
    const w = parent?.clientWidth ?? window.innerWidth;
    const h = parent?.clientHeight ?? window.innerHeight;
    this.cameraRig.cycleViewMode(this.boardSize, w, h, insets);
    this.lighting.updateForCamera(this.cameraRig.camera);
  }

  getCamera(): THREE.PerspectiveCamera {
    return this.cameraRig.camera;
  }

  /** Cell under the given screen point, hit-tested against the sphere-projected dot/path positions. */
  pickCell(clientX: number, clientY: number): Pos | null {
    return this.picker.pick(
      clientX,
      clientY,
      this.cameraRig.camera,
      this.boardGroup,
      this.boardSize,
      this.canvas,
      this.sphereBounds,
    );
  }

  showTouchAt(clientX: number, clientY: number): void {
    const point = this.picker.getHitPoint(
      clientX,
      clientY,
      this.cameraRig.camera,
      this.boardGroup,
      this.boardSize,
      this.canvas,
      this.sphereBounds,
    );
    if (point) {
      this.touchIndicator.showAt(point);
    }
  }

  hideTouch(): void {
    this.touchIndicator.hide();
  }

  private fitCamera(insets?: ViewportInsets): void {
    this.viewportInsets = insets ?? this.viewportInsets;
    const parent = this.canvas.parentElement;
    const w = parent?.clientWidth ?? window.innerWidth;
    const h = parent?.clientHeight ?? window.innerHeight;
    this.cameraRig.resize(w, h, this.boardSize, this.viewportInsets);
    this.lighting.updateForCamera(this.cameraRig.camera);
  }

  private startLoop(): void {
    const tick = (now: number) => {
      this.rotation.tick(now);
      this.lighting.updateForCamera(this.cameraRig.camera);
      if (this.visualStyle === 'gear') {
        this.gearBackground.faceCamera(this.cameraRig.camera);
      }
      this.renderer.render(this.scene, this.cameraRig.camera);
      this.onAfterRender?.();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  destroy(): void {
    cancelAnimationFrame(this.rafId);
    this.core.dispose();
    this.board.dispose();
    this.dots.dispose();
    this.paths.dispose();
    this.rails.dispose();
    this.gearBackground.dispose();
    this.touchIndicator.dispose();
    this.renderer.dispose();
  }
}
